<template>
  <div class="employee-utilization">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <!-- Date Range Filter -->
    <div class="date-filter-container">
      <div class="p-inputgroup">
        <span class="p-inputgroup-addon">
          <i class="pi pi-calendar"></i>
        </span>
        <Calendar
          ref="employeeUtilizationCalendar"
          v-model="dateRange"
          selectionMode="range"
          class="p-column-filter"
          dateFormat="dd.mm.yy"
          :placeholder="$t('date')"
          :showWeek="true"
          :yearNavigator="true"
          :monthNavigator="true"
          v-on:date-select="onDateRangeFilter"
        >
        </Calendar>
      </div>
    </div>

    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="getEmployeeUtilization"
      :loading="isLoading"
      :rowHover="true"
      :lazy="true"
      filterDisplay="row"
      :stateKey="tableStateStore.filterName()"
      :filters.sync="tableStateStore.filters"
      :rows.sync="tableStateStore.pagination.pageSize"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      :first="tableStateStore.skip()"
      stateStorage="local"
      :totalRecords="getEmployeeUtilizationCount"
      :paginator="true"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 20, 50, 100]"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      responsiveLayout="scroll"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h4 class="page-title">{{ $t('employeeUtilization') }}</h4>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse mr-2" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
            <ButtonWait
              :startCallback="startEmployeeUtilizationExportBuild"
              :pollCallback="fetchExportBuildState"
            >
              <template v-slot:buttonText>
                <i class="fa fa-download mr-2"></i>
                {{ $t('generate_employee_utilization_list') }}
              </template>
            </ButtonWait>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <!-- Employee Number -->
      <Column
        field="employee.number"
        :header="$t('employeeNumber')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
        filterField="employeeNumber"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.employee && slotProps.data.employee.number">
            {{ slotProps.data.employee.number.toString().padStart(4, '0') }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <!-- Employee Name -->
      <Column
        field="employee.displayName"
        :header="$t('employee')"
        sortable
        filterField="employeeName"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.employee">
            {{ slotProps.data.employee.firstname }} {{ slotProps.data.employee.lastname }}
          </span>
          <span v-else class="text-muted">{{ $t('unassigned') }}</span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText
            type="text"
            v-model="filterModel.value"
            @input="filterCallback()"
            :placeholder="$t('search')"
          />
        </template>
      </Column>

      <!-- Appointment Type -->
      <Column
        field="appointmentType"
        :header="$t('appointmentType')"
        sortable
        :headerStyle="{ width: '150px' }"
        :bodyStyle="'text-align: center; width: 150px'"
      >
        <template #body="slotProps">
          <b-badge :variant="getAppointmentTypeColor(slotProps.data.appointmentType)">
            {{ $t(slotProps.data.appointmentType) }}
          </b-badge>
        </template>
        <template #filter>
          <MultiSelect
            :value="tableStateStore.customFilters.appointmentTypes"
            :options="appointmentTypes"
            @input="onAppointmentTypeFilter"
            optionLabel="label"
            :placeholder="$t('appointmentType')"
            display="chip"
            :style="{ 'max-width': '150px' }"
          >
            <template #option="slotProps">
              <b-badge :variant="getAppointmentTypeColor(slotProps.option.value)">
                {{ $t(slotProps.option.value) }}
              </b-badge>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Appointments Count -->
      <Column
        field="appointmentCount"
        :header="$t('appointmentsAssigned')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
      >
        <template #body="slotProps">
          <span class="badge badge-primary">
            {{ slotProps.data.appointmentCount }}
          </span>
        </template>
      </Column>

      <!-- Capacity Count -->
      <Column
        field="appointmentCapacityCount"
        :header="$t('totalCapacity')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
      >
        <template #body="slotProps">
          <span class="badge badge-secondary">
            {{ slotProps.data.appointmentCapacityCount }}
          </span>
        </template>
      </Column>

      <!-- Utilization Percentage -->
      <Column
        field="utilizationInPercent"
        :header="$t('utilizationPercent')"
        sortable
        :headerStyle="{ width: '120px' }"
        :bodyStyle="'text-align: center; width: 120px'"
      >
        <template #body="slotProps">
          <span>{{ slotProps.data.utilizationInPercent }}%</span>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Breadcrumbs from '@/components/Breadcrumbs';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Calendar from 'primevue/calendar';
import ButtonWait from '@/components/ButtonWait.vue';
import { getAppointmentTypeColor } from '@/helpers/colors';

export default {
  name: 'EmployeeUtilization',
  components: {
    DataTable,
    Breadcrumbs,
    Column,
    InputText,
    MultiSelect,
    Calendar,
    ButtonWait,
  },

  props: {
    tableStatePrefix: {
      type: String,
      default: 'employee-utilization',
    },
  },

  data() {
    return {
      dateRange: [
        this.$dayjs()
          .startOf('week')
          .toDate(),
        this.$dayjs()
          .startOf('week')
          .add(5, 'days')
          .toDate(),
      ],
      employeeUtilizationTableStateConfig: {
        name: this.tableStatePrefix,
        filters: {
          employeeNumber: { value: null },
          employeeName: { value: null },
          dateRange: { range: null },
        },
        customFilters: {
          appointmentTypes: [],
        },
        sorting: { sortField: 'employee.number', sortOrder: 1 },
      },
      defaultFilters: {
        employeeNumber: { value: null },
        employeeName: { value: null },
        dateRange: { range: null },
      },
      defaultCustomFilters: {
        appointmentTypes: [],
      },
    };
  },

  computed: {
    ...mapGetters(['getEmployeeUtilization', 'getEmployeeUtilizationCount']),
    ...mapGetters(['isLoading', 'getTableState', 'getEnumValues']),
    ...mapGetters('auth', ['getCurrentUser']),

    tableStateStore() {
      return this.getTableState(this.employeeUtilizationTableStateConfig.name);
    },

    breadcrumbs() {
      return [
        { name: 'Home', route: { name: 'home' } },
        { name: this.$t('employees') },
        { name: this.$t('utilization') },
      ];
    },
    appointmentTypes() {
      const appointmentTypes = this.getEnumValues('AppointmentType').map((appointmentType) => {
        return { value: appointmentType, label: this.$t(appointmentType) };
      });
      return appointmentTypes;
    },
  },

  methods: {
    ...mapActions([
      'fetchEmployeeAppointmentUtilization',
      'fetchEnumValues',
      'fetchUserAttachmentUrl',
    ]),
    ...mapActions(['initTableState']),
    ...mapActions('auth', ['buildUserEmployeeUtilizationExport', 'refreshUser']),

    getAppointmentTypeColor,

    /**
     * Load remote table data
     */
    async loadTableData() {
      // const filters = this.buildFilters();

      await this.fetchEmployeeAppointmentUtilization({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: { ...this.tableStateStore.filters, ...this.tableStateStore.customFilters },
      });
    },

    /**
     * Build filters matching the GraphQL schema
     */
    buildFilters() {
      const filters = {};

      // Employee number filter
      if (this.tableStateStore.filters.employeeNumber?.value) {
        filters.employeeNumber = {
          value: this.tableStateStore.filters.employeeNumber.value,
        };
      }

      // Employee name filter
      if (this.tableStateStore.filters.employeeName?.value) {
        filters.employeeName = {
          value: this.tableStateStore.filters.employeeName.value,
        };
      }

      // Appointment types filter (multiselect)
      if (this.tableStateStore.customFilters.appointmentTypes?.length > 0) {
        filters.appointmentTypes = this.tableStateStore.customFilters.appointmentTypes;
      }

      return filters;
    },

    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },

    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },

    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },

    /**
     * Handle appointment type filter changes
     */
    async onAppointmentTypeFilter(value) {
      const filteredAppointmentTypes = {
        appointmentTypes: this.appointmentTypes.filter((appointmentType) =>
          value.includes(appointmentType)
        ),
      };
      this.tableStateStore.setCustomFilters(filteredAppointmentTypes);

      await this.loadTableData();
    },

    /**
     * Handle date range filter changes
     */
    async onDateRangeFilter(value) {
      this.tableStateStore.filters.dateRange.range = this.dateRange;
      await this.loadTableData();
    },

    /**
     * Start employee utilization export build process
     */
    async startEmployeeUtilizationExportBuild() {
      if (this.getCurrentUser) {
        const userId = this.getCurrentUser.id;
        const totalRecords = this.getEmployeeUtilizationCount;

        const pagination = {
          page: 0,
          pageSize: totalRecords > 0 ? totalRecords : this.tableStateStore.pagination.pageSize,
        };

        const sorting = {
          sortField: this.tableStateStore.sorting.sortField,
          sortOrder: this.tableStateStore.sorting.sortOrder,
        };

        const filters = {
          ...this.tableStateStore.filters,
          ...this.tableStateStore.customFilters,
        };

        await this.buildUserEmployeeUtilizationExport({
          userId,
          pagination,
          sorting,
          filters,
        });
      }
    },

    /**
     * Fetch export build state for polling
     */
    async fetchExportBuildState() {
      await this.refreshUser();

      if (this.getCurrentUser.employeeUtilizationExportBuildState === 'FINISHED') {
        this.downloadEmployeeUtilizationExport('employeeUtilizationExportAttachment');
      }
      return this.getCurrentUser.employeeUtilizationExportBuildState;
    },

    /**
     * Download employee utilization export file
     */
    async downloadEmployeeUtilizationExport(attachmentType) {
      try {
        const url = await this.fetchUserAttachmentUrl({
          userId: this.getCurrentUser.id,
          attachmentType: attachmentType,
        });
        window.open(url);
      } catch (error) {
        console.error('Error fetching attachment URL:', error);
      }
    },

    /**
     * Reset filter settings
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
  },

  async created() {
    await this.initTableState(this.employeeUtilizationTableStateConfig);
  },

  async mounted() {
    await this.fetchEnumValues('AppointmentType');

    // Initialize date range filter
    this.dateRange[0] = this.$dayjs()
      .startOf('week')
      .toDate();
    this.dateRange[1] = this.$dayjs(this.dateRange[0])
      .add(5, 'days')
      .toDate();

    this.tableStateStore.filters.dateRange.range = this.dateRange;

    await this.loadTableData();
  },
};
</script>

<style lang="scss">
.p-column-filter-clear-button {
  display: none;
  width: 0;
}

.p-column-filter-menu-button {
  display: none;
  width: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

.table-header-left {
  display: flex;
  justify-content: flex-start;
}

.table-header-right {
  display: flex;
  justify-content: flex-end;
  column-gap: 1em;
}

.date-filter-container {
  margin-bottom: 1em;
  display: flex;
  justify-content: flex-start;
}

.date-filter-container .p-inputgroup {
  max-width: 300px;
}

/** Fix Dropdown overlay and table height */
.p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 37rem;
}
</style>
